import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Camera, Plus, Lightbulb } from "lucide-react";
import {deletePhoto, fetchImages, uploadFileWithMetadata} from "../../../storageService";

// Constantes originales para validación
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
const MAX_WORK_PHOTOS = 8;

const MediaStep = ({ formData, setFormData, errors }) => {
    const [images, setImages] = useState({ profilePhoto: null, workPhotos: [] });
    const [uploadingProfile, setUploadingProfile] = useState(false);
    const [uploadingWork, setUploadingWork] = useState(false);

    // Cargar imágenes existentes (funcionalidad original)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const fetchedImages = await fetchImages(formData.photo, formData.workPhotos);
                setImages(fetchedImages);
            } catch (error) {
                console.error("Error fetching images:", error);
                toast.error("Error al cargar las imágenes");
            }
        };

        if (formData.username && formData.type) {
            fetchData();
        }
    }, [formData.username, formData.type, formData.workPhotos]);

    // Función para verificar si se pueden cargar fotos
    const canUploadPhotos = () => {
        // No permitir carga de fotos si hay errores en el username o si está vacío
        return formData.username && formData.username.trim() !== '' && !errors.username;
    };

    // Función para manejar clics en botones deshabilitados
    const handleDisabledClick = () => {
        if (!formData.username || formData.username.trim() === '') {
            toast.warning('Primero debes completar tu nombre de usuario en el paso "Información Básica"');
        } else if (errors.username) {
            toast.warning('Hay un problema con tu nombre de usuario. Por favor corrígelo antes de cargar fotos');
        }
    };

    // Validación original de archivos
    const validateFile = (file) => {
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`El archivo ${file.name} supera el límite de 8MB.`);
            return false;
        }

        if (!VALID_IMAGE_TYPES.includes(file.type)) {
            toast.error(`El archivo ${file.name} no es de un formato de imagen válido.`);
            return false;
        }

        return true;
    };

    // Manejar foto de perfil (funcionalidad original)
    const handleProfilePhotoChange = async (file) => {
        if (!file || !validateFile(file)) return;

        // Verificar que se pueda cargar fotos (username válido)
        if (!canUploadPhotos()) {
            toast.error('Primero debes completar un nombre de usuario válido');
            return;
        }

        setUploadingProfile(true);
        try {
            const profilePath = `providers/${formData.type}/${formData.username}/profile.jpg`;
            const profilePhotoUrl = await uploadFileWithMetadata(file, profilePath);

            setFormData((prev) => ({ ...prev, photo: profilePath }));
            setImages(prev => ({ ...prev, profilePhoto: profilePhotoUrl }));
            toast.success("Foto de perfil subida con éxito");
        } catch (error) {
            console.error("Error al subir foto de perfil:", error);
            toast.error("No se pudo subir la foto de perfil");
        } finally {
            setUploadingProfile(false);
        }
    };

    // Manejar fotos de trabajo (funcionalidad original)
    const handleWorkPhotosChange = async (files) => {
        if (!files.length) return;

        // Verificar que se pueda cargar fotos (username válido)
        if (!canUploadPhotos()) {
            toast.error('Primero debes completar un nombre de usuario válido');
            return;
        }

        const currentPhotoCount = formData.workPhotos ? formData.workPhotos.length : 0;
        if (currentPhotoCount >= MAX_WORK_PHOTOS) {
            toast.error(`Has alcanzado el límite máximo de ${MAX_WORK_PHOTOS} fotos de trabajo.`);
            return;
        }

        const remainingSlots = MAX_WORK_PHOTOS - currentPhotoCount;
        let validFiles = Array.from(files).filter(validateFile);

        if (validFiles.length > remainingSlots) {
            toast.warning(`Solo se cargarán las primeras ${remainingSlots} imágenes para no superar el límite de ${MAX_WORK_PHOTOS} fotos.`);
            validFiles = validFiles.slice(0, remainingSlots);
        }

        if (validFiles.length === 0) return;

        setUploadingWork(true);
        try {
            const newPhotos = await Promise.all(
                validFiles.map(async (file) => {
                    const timestamp = Date.now();
                    const workPhotoPath = `providers/${formData.type}/${formData.username}/work_photo_${timestamp}`;
                    const workPhotoUrl = await uploadFileWithMetadata(file, workPhotoPath);
                    return { workPhotoPath, workPhotoUrl };
                })
            );

            setFormData((prev) => ({
                ...prev,
                workPhotos: [...(prev.workPhotos || []), ...newPhotos.map(photo => photo.workPhotoPath)],
            }));

            setImages((prev) => ({
                ...prev,
                workPhotos: [...prev.workPhotos, ...newPhotos.map(photo => photo.workPhotoUrl)],
            }));

            toast.success("Fotos de trabajos subidas con éxito");
        } catch (error) {
            console.error("Error al subir fotos de trabajos:", error);
            toast.error("No se pudieron subir algunas fotos");
        } finally {
            setUploadingWork(false);
        }
    };

    // Eliminar foto (funcionalidad original)
    const handleDeletePhoto = async (photo, index) => {
        try {
            await deletePhoto(photo);
            setFormData((prev) => ({
                ...prev,
                workPhotos: prev.workPhotos.filter((_, photoIndex) => photoIndex !== index)
            }));
            setImages((prev) => ({
                ...prev,
                workPhotos: prev.workPhotos.filter((_, photoIndex) => photoIndex !== index)
            }));
            toast.success("Foto eliminada con éxito");
        } catch (error) {
            console.error("Error al eliminar la foto:", error);
            toast.error("No se pudo eliminar la foto");
        }
    };

    return (
        <div className="step-content-wrapper">
            <div className="step-header">
                <h2>Fotos</h2>
                <p>Sube tu foto de perfil y muestra ejemplos de tu trabajo</p>
            </div>

            <div className="form-section">
                {/* Mensaje de aviso cuando no se pueden cargar fotos */}
                {!canUploadPhotos() && (
                    <div className="form-group">
                        <div className="field-warning" style={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '4px',
                            padding: '12px',
                            marginBottom: '20px',
                            color: '#856404'
                        }}>
                            <strong>⚠️ No puedes cargar fotos aún</strong>
                            <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                                {!formData.username || formData.username.trim() === ''
                                    ? 'Primero debes completar tu nombre de usuario en el paso "Información Básica".'
                                    : 'Hay un problema con tu nombre de usuario. Por favor corrígelo en el paso "Información Básica" antes de cargar fotos.'
                                }
                            </p>
                        </div>
                    </div>
                )}

                {/* Foto de perfil */}
                <div className="form-group">
                    <label>
                        Foto de perfil <span className="required">*</span>
                    </label>
                    <div className="field-help">
                        Formato: JPG, PNG, GIF, WebP, SVG, BMP. Máximo 8MB.
                    </div>

                    <div className="photo-upload-area">
                        {images.profilePhoto ? (
                            <div className="profile-photo-preview">
                                <img src={images.profilePhoto} alt="Foto de perfil" />
                                <div className="photo-actions">
                                    <label
                                        className={`upload-btn ${!canUploadPhotos() ? 'disabled' : ''}`}
                                        onClick={!canUploadPhotos() ? handleDisabledClick : undefined}
                                        style={!canUploadPhotos() ? { cursor: 'not-allowed' } : {}}
                                    >
                                        {uploadingProfile ? "Subiendo..." : "Cambiar foto"}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleProfilePhotoChange(e.target.files[0])}
                                            disabled={uploadingProfile || !canUploadPhotos()}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                </div>
                            </div>
                        ) : (
                            <div className="upload-placeholder">
                                <div className="upload-icon"><Camera size={40} /></div>
                                <p>Sube tu foto de perfil</p>
                                <label
                                    className={`upload-btn primary ${!canUploadPhotos() ? 'disabled' : ''}`}
                                    onClick={!canUploadPhotos() ? handleDisabledClick : undefined}
                                    style={!canUploadPhotos() ? { cursor: 'not-allowed' } : {}}
                                >
                                    {uploadingProfile ? "Subiendo..." : !canUploadPhotos() ? "Completa tu nombre de usuario primero" : "Seleccionar foto"}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleProfilePhotoChange(e.target.files[0])}
                                        disabled={uploadingProfile || !canUploadPhotos()}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="field-footer">
                        {errors.photo && <span className="error-message">{errors.photo}</span>}
                    </div>
                </div>

                {/* Fotos de trabajos */}
                <div className="form-group">
                    <label>
                        Fotos de trabajos
                        <span className="photo-counter">
                            ({formData.workPhotos ? formData.workPhotos.length : 0}/{MAX_WORK_PHOTOS})
                        </span>
                    </label>
                    <div className="field-help">
                        Muestra ejemplos de tu trabajo. Hasta {MAX_WORK_PHOTOS} fotos, máximo 8MB cada una.
                    </div>

                    <div className="work-photos-section">
                        {images.workPhotos.length > 0 && (
                            <div className="work-photos-grid">
                                {images.workPhotos.map((photo, index) => (
                                    <div key={index} className="work-photo-item">
                                        <img src={photo} alt={`Trabajo ${index + 1}`} />
                                        <button
                                            type="button"
                                            onClick={() => handleDeletePhoto(photo, index)}
                                            className="delete-photo-btn"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {formData.workPhotos && formData.workPhotos.length >= MAX_WORK_PHOTOS ? (
                            <div className="upload-limit-reached">
                                <p>Has alcanzado el límite de {MAX_WORK_PHOTOS} fotos</p>
                                <p>Elimina alguna para subir más</p>
                            </div>
                        ) : (
                            <div className="upload-area">
                                <label
                                    className={`upload-btn outline ${!canUploadPhotos() ? 'disabled' : ''}`}
                                    onClick={!canUploadPhotos() ? handleDisabledClick : undefined}
                                    style={!canUploadPhotos() ? { cursor: 'not-allowed' } : {}}
                                >
                                    <Plus size={16} style={{marginRight: '4px'}} />
                                    {uploadingWork ? "Subiendo..." : !canUploadPhotos() ? "Completa tu nombre de usuario primero" : "Agregar fotos"}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleWorkPhotosChange(e.target.files)}
                                        disabled={uploadingWork || !canUploadPhotos()}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                <div className="media-tips">
                    <h4><Lightbulb size={16} style={{marginRight: '4px'}} /> Consejos para mejores fotos</h4>
                    <ul>
                        <li>Usa buena iluminación natural</li>
                        <li>Enfoca en los detalles de calidad</li>
                        <li>Muestra el antes y después cuando sea posible</li>
                        <li>Evita fotos borrosas o muy oscuras</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default MediaStep;
